/**
 * @file shop-nav-registry.ts
 * @description Реестр меню кабинета магазина (phase19.31, SC14–SC18, SC17).
 *
 * Меню — ДАННЫЕ, а не разметка: новый раздел = запись (путь, значок,
 * подпись, права, счётчик, синонимы для поиска). Права пункта совпадают с
 * правами его маршрута в `router.tsx` (И — все перечисленные): пункт не
 * ведёт туда, где пользователь получит «Нет доступа».
 *
 * Состав и названия — uiux-design.md §3.2 и SC17 (утверждено пользователем
 * 2026-10-02). Группы: Сводка, Заказы, Клиенты, Товары, Склад, Сайт,
 * Каналы продаж, Настройки (SC14). «Настройки» в панели — один пункт на
 * страницу-список разделов (SC15), разделы — `SHOP_SETTINGS_HUB`.
 *
 * Не добавлены (и почему):
 *  - «Возвраты» — отдельной страницы возвратов нет: доска возвратов — режим
 *    «Доски заказов» (`orders/board?type=returns`), старый адрес
 *    `orders/returns/board` лишь перенаправляет туда;
 *  - «Оформление кабинета» — страница появится следующим шагом фазы.
 */

import {
  AlertTriangle,
  ArrowDownToLine,
  ArrowLeftRight,
  BadgeCheck,
  Bell,
  BookOpen,
  Boxes,
  ClipboardList,
  Factory,
  FileText,
  Files,
  FolderTree,
  Globe,
  History,
  Kanban,
  LayoutDashboard,
  LayoutTemplate,
  ListChecks,
  MapPin,
  Megaphone,
  MessageSquareQuote,
  Package,
  PenSquare,
  Percent,
  Receipt,
  Scale,
  Search,
  Settings,
  Shapes,
  ShoppingCart,
  Split,
  Star,
  Store,
  Tag,
  Trash2,
  Truck,
  UserCircle,
  Users,
  Wallet,
  Warehouse,
  Workflow,
  type LucideIcon,
} from "lucide-react";

/** Счётчики меню — поля ответа `GET /admin/nav-counters` (SC18, SC41). */
export type NavCounterKey = "ordersAttention" | "reviewsPending" | "chatUnread" | "pickingQueue";

/** Пункт меню (или раздел страницы «Настройки»). */
export interface ShopNavItem {
  id: string;
  label: string;
  /** Путь внутри магазина без `/shop/:slug/`; `""` — главная («Сводка»). */
  path: string;
  icon: LucideIcon;
  /** Права (все), как у маршрута; нет — пункт виден всем сотрудникам. */
  permissions?: readonly string[];
  /** Хотя бы одно из прав (конструктор сайта: контент или настройки, phase19.16 Р1-6а). */
  anyPermissions?: readonly string[];
  /** Активен только на точном адресе (без вложенных страниц). */
  end?: boolean;
  /** Вложенные пути, на которых пункт НЕ подсвечивается (у них свой пункт). */
  excludePaths?: readonly string[];
  counter?: NavCounterKey;
  /** Подзаголовок внутри группы («Доставка»). */
  section?: string;
  /** Синонимы для поиска по меню («ТК», «ПВЗ», «блог»). */
  keywords?: readonly string[];
  /** Пояснение — на странице «Настройки» (в списке разделов до 1280px). */
  description?: string;
}

/** Сворачиваемая группа меню. */
export interface ShopNavGroup {
  id: string;
  label: string;
  icon: LucideIcon;
  items: readonly ShopNavItem[];
}

const ORDERS = ["SHOP_VIEW_ORDERS"] as const;
const CRM = ["SHOP_USE_CRM"] as const;
const CATALOG = ["SHOP_MANAGE_CATALOG"] as const;
const WMS = ["SHOP_WMS_VIEW"] as const;
const CONTENT = ["SHOP_MANAGE_CONTENT"] as const;
const SETTINGS = ["SHOP_MANAGE_SETTINGS"] as const;
const CARRIERS = ["SHOP_VIEW_ORDERS", "SHOP_MANAGE_CARRIERS"] as const;

/** «Сводка» — главная магазина, первым пунктом без группы. */
export const SHOP_NAV_HOME: ShopNavItem = {
  id: "home",
  label: "Сводка",
  path: "",
  icon: LayoutDashboard,
  end: true,
  keywords: ["дашборд", "главная"],
};

/** Группы меню в порядке SC14. */
export const SHOP_NAV_GROUPS: readonly ShopNavGroup[] = [
  {
    id: "orders",
    label: "Заказы",
    icon: ShoppingCart,
    items: [
      { id: "orders", label: "Заказы", path: "orders", icon: Package, permissions: ORDERS, end: true },
      { id: "orders-board", label: "Доска заказов", path: "orders/board", icon: Kanban, permissions: ORDERS, keywords: ["канбан", "возвраты"] },
      { id: "orders-attention", label: "Требуют внимания", path: "orders/attention", icon: AlertTriangle, permissions: ORDERS, counter: "ordersAttention" },
      // Реестр документов живёт под /crm — маршрут требует и CRM, и документы.
      { id: "documents", label: "Документы", path: "crm/documents", icon: Files, permissions: ["SHOP_USE_CRM", "SHOP_VIEW_DOCUMENTS"], keywords: ["упд", "счёт", "реестр"] },
      { id: "carriers", label: "Транспортные компании", path: "carriers", icon: Truck, permissions: CARRIERS, end: true, section: "Доставка", keywords: ["тк", "доставка", "перевозчик", "сдэк"] },
      { id: "pickup-points", label: "Пункты выдачи", path: "carriers/pickup-points", icon: MapPin, permissions: CARRIERS, section: "Доставка", keywords: ["пвз", "доставка"] },
    ],
  },
  {
    id: "customers",
    label: "Клиенты",
    icon: Users,
    items: [
      { id: "customers", label: "Клиенты", path: "customers", icon: Users, permissions: ["SHOP_VIEW_CUSTOMERS"], keywords: ["покупатели"] },
      // «Сообщения» — не пункт меню, а окно с кнопкой в углу (SC64); счётчик chatUnread — на кнопке.
      { id: "deals", label: "Сделки", path: "crm/deals", icon: Receipt, permissions: CRM, keywords: ["crm", "срм"] },
      { id: "deals-board", label: "Доска сделок", path: "crm/board", icon: Kanban, permissions: CRM, keywords: ["канбан", "crm", "срм"] },
      { id: "activities", label: "Задачи", path: "crm/activities", icon: ListChecks, permissions: CRM, keywords: ["активности", "звонки", "встречи"] },
      { id: "notification-log", label: "Журнал уведомлений", path: "settings/notifications", icon: History, permissions: SETTINGS, keywords: ["уведомления", "приглашения"] },
    ],
  },
  {
    id: "catalog",
    label: "Товары",
    icon: Package,
    items: [
      { id: "products", label: "Товары", path: "catalog/products", icon: Package, permissions: CATALOG, keywords: ["каталог"] },
      { id: "categories", label: "Категории", path: "catalog/categories", icon: FolderTree, permissions: CATALOG },
      { id: "product-kinds", label: "Виды товара", path: "catalog/product-kinds", icon: Shapes, permissions: CATALOG },
      { id: "tags", label: "Теги", path: "catalog/tags", icon: Tag, permissions: CATALOG },
      { id: "producers", label: "Производители", path: "catalog/producers", icon: Factory, permissions: CATALOG },
      { id: "suppliers", label: "Поставщики", path: "catalog/suppliers", icon: Truck, permissions: CATALOG },
      { id: "promotions", label: "Акции", path: "catalog/promotions", icon: Percent, permissions: CATALOG, keywords: ["скидки"] },
    ],
  },
  {
    id: "wms",
    label: "Склад",
    icon: Warehouse,
    items: [
      { id: "wms", label: "Обзор склада", path: "wms", icon: LayoutDashboard, permissions: WMS, end: true, keywords: ["дашборд склада"] },
      { id: "stock", label: "Остатки", path: "wms/stock", icon: Package, permissions: WMS },
      { id: "warehouses", label: "Склады", path: "wms/warehouses", icon: Warehouse, permissions: WMS },
      { id: "receipts", label: "Приёмка", path: "wms/receipts", icon: ArrowDownToLine, permissions: WMS },
      { id: "picking", label: "Сборка", path: "wms/picking", icon: ClipboardList, permissions: WMS, counter: "pickingQueue" },
      { id: "bins", label: "Ячейки", path: "wms/bins", icon: Boxes, permissions: WMS },
      { id: "transfers", label: "Перемещения", path: "wms/transfers", icon: ArrowLeftRight, permissions: WMS },
      { id: "expiring", label: "Срок годности", path: "wms/expiring", icon: AlertTriangle, permissions: WMS },
      { id: "writeoffs", label: "Списания", path: "wms/writeoffs", icon: Trash2, permissions: WMS },
      { id: "channel-reserves", label: "Резервы по каналам", path: "wms/channel-reserves", icon: Split, permissions: WMS, keywords: ["каналы"] },
      { id: "wms-settings", label: "Настройки склада", path: "wms/settings", icon: Settings, permissions: WMS },
    ],
  },
  {
    id: "site",
    label: "Сайт",
    icon: Globe,
    items: [
      // phase19.16 Р1-6а (uiux-builder-shell.md §4) — одно окно вместо семи экранов: страницы, блоки и
      // вид сайта. Права — по частям внутри (контент или настройки магазина). Слова для поиска —
      // прежние названия разделов, чтобы «шрифты» находили конструктор («SEO» — свой пункт ниже, Р3в).
      { id: "site-builder", label: "Конструктор сайта", path: "site/builder", icon: LayoutTemplate, anyPermissions: [...CONTENT, ...SETTINGS], section: "Вид сайта", keywords: ["страницы", "оформление", "цвета", "шрифты", "шапка", "подвал", "логотип", "меню сайта", "настройки сайта", "лента блога", "rss", "дизайн"] },
      // Р3в (SB145, SC14): SEO — отдельно от конструктора, сразу после него.
      { id: "site-seo", label: "SEO", path: "site/seo", icon: Search, anyPermissions: [...CONTENT, ...SETTINGS], section: "Вид сайта", keywords: ["поиск", "яндекс", "google", "переадресации", "редиректы", "301", "индексация", "вебмастер", "robots", "sitemap", "карта сайта"] },
      { id: "articles", label: "Статьи", path: "content/articles", icon: PenSquare, permissions: CONTENT, section: "Блог", keywords: ["блог"] },
      { id: "authors", label: "Авторы", path: "content/authors", icon: UserCircle, permissions: CONTENT, section: "Блог", keywords: ["блог"] },
      { id: "content-categories", label: "Рубрики блога", path: "content/categories", icon: BookOpen, permissions: CONTENT, section: "Блог", keywords: ["категории блога", "блог"] },
      { id: "content-tags", label: "Теги блога", path: "content/tags", icon: Tag, permissions: CONTENT, section: "Блог", keywords: ["блог"] },
      { id: "reviews", label: "Отзывы о товарах", path: "content/reviews", icon: Star, permissions: CONTENT, section: "Отзывы", counter: "reviewsPending", keywords: ["модерация"] },
      { id: "testimonials", label: "Отзывы на главной", path: "content/testimonials", icon: MessageSquareQuote, permissions: CONTENT, section: "Отзывы", keywords: ["свидетельства"] },
    ],
  },
  {
    id: "channels",
    label: "Каналы продаж",
    icon: Megaphone,
    items: [
      { id: "avito", label: "Avito", path: "avito", icon: Megaphone, permissions: ["SHOP_AVITO_CABINET_VIEW"], excludePaths: ["avito/finance"], keywords: ["авито"] },
      { id: "avito-finance", label: "Финансы Avito", path: "avito/finance", icon: Wallet, permissions: ["SHOP_AVITO_CABINET_VIEW", "SHOP_AVITO_FINANCE_VIEW"], keywords: ["авито"] },
    ],
  },
];

/** Пункт «Настройки» — страница-список разделов (SC15). */
export const SHOP_NAV_SETTINGS: ShopNavItem = {
  id: "settings",
  label: "Настройки",
  path: "settings",
  icon: Settings,
  // «Журнал уведомлений» лежит под settings/, но это пункт группы «Клиенты».
  excludePaths: ["settings/notifications"],
  keywords: ["настройки магазина"],
};

/** Группа разделов страницы «Настройки». */
export interface SettingsHubGroup {
  id: string;
  label: string;
  items: readonly ShopNavItem[];
}

/** Разделы «Настроек»: каждый со своими правами, как его маршрут (SC15). */
export const SHOP_SETTINGS_HUB: readonly SettingsHubGroup[] = [
  {
    id: "shop",
    label: "Магазин",
    items: [
      { id: "shop-settings", label: "Магазин", path: "settings/shop", icon: Store, permissions: SETTINGS, description: "Название, контакты и общие настройки", keywords: ["настройки магазина"] },
      { id: "users", label: "Сотрудники", path: "users", icon: BadgeCheck, permissions: ["SHOP_MANAGE_USERS"], description: "Кто работает в кабинете и с какими правами", keywords: ["пользователи", "роли"] },
      // phase19.28 этап А: адреса сайта — только чтение и заявка на свой домен (PL11).
      { id: "domains", label: "Домены", path: "settings/domains", icon: Globe, permissions: SETTINGS, description: "Адреса сайта и заявка на подключение своего домена", keywords: ["домен", "адрес сайта"] },
      { id: "legal-profiles", label: "Реквизиты", path: "settings/legal-profiles", icon: Receipt, permissions: SETTINGS, description: "Юридические лица магазина и банковские счета", keywords: ["инн", "юр.лицо"] },
      { id: "legal-documents", label: "Правовые документы", path: "settings/legal-documents", icon: Scale, permissions: SETTINGS, description: "Оферта, политика обработки данных и другие тексты", keywords: ["юр.документы", "оферта", "политика"] },
    ],
  },
  {
    id: "processes",
    label: "Заказы и сделки",
    items: [
      { id: "order-stages", label: "Стадии заказов", path: "orders/pipelines", icon: Workflow, permissions: ["SHOP_VIEW_ORDERS", "SHOP_MANAGE_ORDERS"], description: "Этапы, через которые проходит заказ" },
      { id: "return-stages", label: "Стадии возвратов", path: "orders/returns/pipelines", icon: Workflow, permissions: ["SHOP_VIEW_ORDERS", "SHOP_MANAGE_ORDERS"], description: "Этапы обработки возврата" },
      { id: "deal-pipelines", label: "Воронки сделок", path: "crm/pipelines", icon: Workflow, permissions: CRM, description: "Воронки и стадии сделок", keywords: ["воронки"] },
    ],
  },
  {
    id: "messages",
    label: "Документы и уведомления",
    items: [
      { id: "document-templates", label: "Шаблоны документов", path: "crm/templates", icon: FileText, permissions: ["SHOP_USE_CRM", "SHOP_MANAGE_CRM_CONFIG"], description: "Счета, акты и другие печатные формы", keywords: ["шаблоны"] },
      { id: "notification-templates", label: "Шаблоны уведомлений", path: "settings/notification-templates", icon: Bell, permissions: SETTINGS, description: "Письма и сообщения клиентам", keywords: ["шаблоны", "письма"] },
    ],
  },
];
